import { derived, readable, type Readable } from "svelte/store";
import { AvailabilityStatus } from "@workadventure/messages";
import { gameManager } from "../../Phaser/Game/GameManager";
import type { MessageUserJoined } from "../../Connection/ConnexionModels";
import { localUserStore } from "../../Connection/LocalUserStore";
import { activeSpacesStore, type ActiveSpaceEntry } from "./ActiveSpacesStore";

export type MeetingGroupParticipant = {
    userId?: number;
    name: string;
    uuid: string;
    userUuid: string;
    pictureStore: Readable<string | undefined>;
    availabilityStatus: AvailabilityStatus;
};

export type MeetingGroup = {
    groupId: number;
    zoneName: string | undefined;
    participants: MeetingGroupParticipant[];
    x: number;
    y: number;
};

export type SpaceMeetingGroup = {
    spaceName: string;
    zoneName: string | undefined;
    participants: MeetingGroupParticipant[];
};

export type AnyMeetingGroup = MeetingGroup | SpaceMeetingGroup;

function getRemotePlayerData(userId: number): MessageUserJoined | undefined {
    try {
        const gameScene = gameManager.getCurrentGameScene();
        return gameScene.getRemotePlayersRepository().getPlayers().get(userId);
    } catch {
        return undefined;
    }
}

function getPlayerPictureStore(userId: number): Readable<string | undefined> {
    try {
        const gameScene = gameManager.getCurrentGameScene();
        return gameScene.MapPlayersByKey.getNestedStore(userId, (item) => item.pictureStore);
    } catch {
        return readable(undefined);
    }
}

function getLocalPlayerPictureStore(): Readable<string | undefined> {
    try {
        const gameScene = gameManager.getCurrentGameScene();
        return gameScene.CurrentPlayer.pictureStore;
    } catch {
        return readable(undefined);
    }
}

function buildMeetingGroups(entries: Map<string, ActiveSpaceEntry>): AnyMeetingGroup[] {
    const groups: AnyMeetingGroup[] = [];

    let myUserId: number | undefined;
    try {
        const gameScene = gameManager.getCurrentGameScene();
        myUserId = gameScene.connection?.getUserId();
    } catch {
        return groups;
    }

    const localUser = localUserStore.getLocalUser();
    const myName = localUserStore.getName();
    const myUuid = localUser?.uuid;

    for (const [, entry] of entries) {
        if (entry.participants.length < 2) continue;

        const participants: MeetingGroupParticipant[] = [];

        if (entry.source === "bubble") {
            for (const participant of entry.participants) {
                const userId = participant.userId;
                if (userId === undefined) continue;

                if (userId === myUserId && myName && myUuid) {
                    participants.push({
                        userId,
                        name: myName,
                        uuid: myUuid,
                        userUuid: myUuid,
                        pictureStore: getLocalPlayerPictureStore(),
                        availabilityStatus: AvailabilityStatus.ONLINE,
                    });
                } else {
                    const playerData = getRemotePlayerData(userId);
                    if (!playerData) continue;
                    participants.push({
                        userId,
                        name: playerData.name,
                        uuid: playerData.userUuid,
                        userUuid: playerData.userUuid,
                        pictureStore: getPlayerPictureStore(userId),
                        availabilityStatus: playerData.availabilityStatus,
                    });
                }
            }

            if (participants.length < 2) continue;

            groups.push({
                groupId: entry.groupId ?? 0,
                zoneName: entry.zoneName,
                participants,
                x: entry.x ?? 0,
                y: entry.y ?? 0,
            });
        } else if (entry.source === "map-area") {
            for (const participant of entry.participants) {
                const userId = participant.userId;
                if (userId === undefined) continue;

                if (userId === myUserId && myName && myUuid) {
                    participants.push({
                        userId,
                        name: myName,
                        uuid: myUuid,
                        userUuid: myUuid,
                        pictureStore: getLocalPlayerPictureStore(),
                        availabilityStatus: AvailabilityStatus.ONLINE,
                    });
                } else {
                    const playerData = getRemotePlayerData(userId);
                    if (!playerData) continue;
                    participants.push({
                        userId,
                        name: playerData.name,
                        uuid: playerData.userUuid,
                        userUuid: playerData.userUuid,
                        pictureStore: getPlayerPictureStore(userId),
                        availabilityStatus: playerData.availabilityStatus,
                    });
                }
            }

            if (participants.length < 2) continue;

            groups.push({
                spaceName: entry.spaceName ?? "",
                zoneName: entry.zoneName,
                participants,
            });
        }
    }

    return groups;
}

export const meetingGroupsStore: Readable<AnyMeetingGroup[]> = derived(activeSpacesStore, ($entries) =>
    buildMeetingGroups($entries),
);
